import { useState } from 'react';
import {
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type Announcements,
  type DragEndEvent,
  type DragStartEvent,
  type ScreenReaderInstructions,
} from '@dnd-kit/core';
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import type { TFunction } from 'i18next';

export function useColumnDragReorder(
  unpinnedColumnKeys: string[],
  onReorderEnd: (newOrder: string[]) => void
) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const [activeDragColumnKey, setActiveDragColumnKey] = useState<string | null>(
    null
  );

  function handleDragStart(event: DragStartEvent) {
    setActiveDragColumnKey(event.active.id as string);
  }

  function handleDragEnd(event: DragEndEvent) {
    setActiveDragColumnKey(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = unpinnedColumnKeys.indexOf(active.id as string);
    const newIndex = unpinnedColumnKeys.indexOf(over.id as string);
    onReorderEnd(arrayMove(unpinnedColumnKeys, oldIndex, newIndex));
  }

  return { sensors, activeDragColumnKey, handleDragStart, handleDragEnd };
}

// dnd-kit's default a11y announcements are English-only and speak the raw column key.
export function getColumnDragAccessibility(
  t: TFunction,
  getColumnLabel: (columnKey: string) => string
): {
  announcements: Announcements;
  screenReaderInstructions: ScreenReaderInstructions;
} {
  return {
    screenReaderInstructions: {
      draggable: t('general:table.column_drag.instructions'),
    },
    announcements: {
      onDragStart: ({ active }) =>
        t('general:table.column_drag.picked_up', {
          column: getColumnLabel(String(active.id)),
        }),
      onDragOver: ({ active, over }) =>
        over
          ? t('general:table.column_drag.dragging_over', {
              column: getColumnLabel(String(active.id)),
              overColumn: getColumnLabel(String(over.id)),
            })
          : t('general:table.column_drag.dragging_over_none', {
              column: getColumnLabel(String(active.id)),
            }),
      onDragEnd: ({ active, over }) =>
        over
          ? t('general:table.column_drag.dropped', {
              column: getColumnLabel(String(active.id)),
              overColumn: getColumnLabel(String(over.id)),
            })
          : t('general:table.column_drag.dropped_none', {
              column: getColumnLabel(String(active.id)),
            }),
      onDragCancel: ({ active }) =>
        t('general:table.column_drag.cancelled', {
          column: getColumnLabel(String(active.id)),
        }),
    },
  };
}
